// src/components/programs/ProgramForm/ProgramFormWrapper.jsx

import React from "react";
import { Box, Stepper, Step, StepLabel, Button, Paper, Alert, CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";
import useFormLanguage from "../../../hooks/forms/useFormLanguage";

// Import steps
import Step0ProgramInfo from "./Step0ProgramInfo";
import Step1ProgramData from "./Step1ProgramData";
import Step2Quotation from "./Step2Quotation";

// Import hooks
import useProgramForm from "../../../hooks/programs/useProgramForm";
import useCreateProgram from "../../../hooks/programs/useCreateProgram";
import useUpdateProgram from "../../../hooks/programs/useUpdateProgram";

const ProgramFormWrapper = ({ isEdit = false, currentData = null }) => {
    const navigate = useNavigate();
    const { isArabic, activeLang, setActiveLang } = useFormLanguage();

    // Form hook
    const {
        currentStep,
        formData,
        setFormData,
        errors,
        setErrors,
        // validateStep,
        handleNext,
        handleBack,
        buildPayload,
    } = useProgramForm(isEdit, currentData, isArabic);

    // Mutations
    const { createProgramMutate, isPending: isCreating } = useCreateProgram();

    const { updateProgramMutate, isPending: isUpdating } = useUpdateProgram(() => {
        navigate("/programs");
    });

    // Callback to clear errors from parent when user edits fields
    const handleClearErrors = React.useCallback((fieldNames) => {
        const newErrors = { ...errors };
        fieldNames.forEach(field => {
            delete newErrors[field];
        });
        setErrors(newErrors);
    }, [errors, setErrors]);

    // Steps configuration
    const steps = [
        { label: isArabic ? "معلومات البرنامج" : "Program Info" },
        { label: isArabic ? "بيانات البرنامج" : "Program Data" },
        { label: isArabic ? "التسعير" : "Quotation" },
    ];

    /**
     * Handle submit
     */
    const handleSubmit = () => {
        const payload = buildPayload();

        if (isEdit) {
            updateProgramMutate({ id: currentData.id, req: payload });
        } else {
            createProgramMutate(payload);
        }
    };

    /**
     * Render step content
     */
    const renderStepContent = ({ activeLang, setActiveLang } = {}) => {
        switch (currentStep) {
            case 0:
                return (
                    <Step0ProgramInfo
                        formData={formData}
                        setFormData={setFormData}
                        errors={errors}
                        isEdit={isEdit}
                        currentData={currentData}
                        onClearErrors={handleClearErrors}
                        activeLang={activeLang}
                        setActiveLang={setActiveLang}
                        isArabic={isArabic}
                    />
                );
            case 1:
                return (
                    <Step1ProgramData
                        formData={formData}
                        setFormData={setFormData}
                        errors={errors}
                        isEdit={isEdit}
                        currentData={currentData}
                        activeLang={activeLang}
                        setActiveLang={setActiveLang}
                    />
                );
            case 2:
                return (
                    <Step2Quotation
                        formData={formData}
                        setFormData={setFormData}
                        errors={errors}
                        isEdit={isEdit}
                        currentData={currentData}
                        activeLang={activeLang}
                    />
                );
            default:
                return null;
        }
    };

    const isLoading = isCreating || isUpdating;

    return (
        <Box>
            {/* Stepper */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Stepper activeStep={currentStep}>
                    {steps.map((step, index) => (
                        <Step key={index}>
                            <StepLabel>{step.label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
            </Paper>

            {/* Error Alert */}
            {Object.keys(errors).length > 0 && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {isArabic
                        ? "يرجى تصحيح الأخطاء قبل المتابعة"
                        : "Please correct the errors before proceeding"}
                </Alert>
            )}

            {/* Step Content */}
            <Box sx={{ mb: 3 }}>
                {renderStepContent({ activeLang, setActiveLang })}
            </Box>

            {/* Navigation Buttons */}
            <Paper sx={{ p: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Button
                        disabled={currentStep === 0 || isLoading}
                        onClick={handleBack}
                        variant="outlined"
                    >
                        {isArabic ? "السابق" : "Back"}
                    </Button>

                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                            variant="outlined"
                            onClick={() => navigate("/programs")}
                            disabled={isLoading}
                        >
                            {isArabic ? "إلغاء" : "Cancel"}
                        </Button>

                        {currentStep === steps.length - 1 ? (
                            <Button
                                variant="contained"
                                onClick={handleSubmit}
                                disabled={isLoading}
                                startIcon={isLoading ? <CircularProgress size={20} /> : null}
                            >
                                {isLoading
                                    ? (isArabic ? "جارٍ الحفظ..." : "Saving...")
                                    : isEdit
                                    ? (isArabic ? "تحديث" : "Update")
                                    : (isArabic ? "حفظ" : "Save")}
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                onClick={() => handleNext(activeLang)}
                                disabled={isLoading}
                            >
                                {isArabic ? "التالي" : "Next"}
                            </Button>
                        )}
                    </Box>
                </Box>
            </Paper>
        </Box>
    );
};

export default ProgramFormWrapper;
